import React, { useLayoutEffect, useRef, useState } from 'react'
import { FileThumbnail } from 'components/ui/FileThumbnail'
import { getFileMetadata } from 'components/UploadedFileList/fileMetadata'
import { useCreationText } from '../../hooks/useCreationText'
import styles from './styles.module.css'

interface IPreviewAttachmentsProps {
  images: string[]
  videos: string[]
  files: string[]
}

export function PreviewAttachments({ images, videos, files }: IPreviewAttachmentsProps) {
  const t = useCreationText()
  const [slots, setSlots] = useState(1)
  const stripRef = useRef<HTMLDivElement>(null)

  const attachments = [
    ...images.map((url) => ({ url, type: 'image' })),
    ...videos.map((url) => ({ url, type: 'video' })),
    ...files.map((url) => ({ url, type: 'file' })),
  ]
  const visibleCount = attachments.length <= slots ? attachments.length : slots - 1
  const hiddenCount = attachments.length - visibleCount

  useLayoutEffect(() => {
    const strip = stripRef.current
    if (!strip) return

    const measure = () => {
      const style = getComputedStyle(strip)
      const size = parseFloat(style.getPropertyValue('--preview-size')) || 24
      const gap = parseFloat(style.columnGap) || 6
      setSlots(Math.max(1, Math.floor((strip.clientWidth + gap) / (size + gap))))
    }

    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(strip)
    window.addEventListener('resize', measure)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  return (
    <div ref={stripRef} className={styles.cardRefs}>
      {attachments.slice(0, visibleCount).map(({ url, type }, index) => {
        if (type === 'image') return <img key={`${type}-${index}`} src={url} alt="" />
        if (type === 'video')
          return <video key={`${type}-${index}`} src={`${url}#t=0.1`} muted playsInline preload="metadata" />

        const { name, mime } = getFileMetadata(url)
        return (
          <span key={`${type}-${index}`} className={styles.cardFile}>
            <FileThumbnail name={name} mime={mime} compact />
          </span>
        )
      })}
      {hiddenCount > 0 && (
        <span className={styles.cardOverflow} role="img" aria-label={`${hiddenCount} ${t.moreAttachments}`}>
          +{hiddenCount}
        </span>
      )}
    </div>
  )
}
